import React from 'react';
import {
    Page,
    Stack,
    InlineError,
    TextField,
    Layout,
    Card,
    Select,
    FormLayout,
    Button
} from '@shopify/polaris';
import { apiFetch as fetch, ResourceTypes, ResourceTypeSettings } from '../../common/Helpers';
import ChooseTagsModal from '../../components/ChooseTagsModal';
import { GenieTag } from '../../components/GenieTag';
import { Helmet } from 'react-helmet';
import ResourceConditionsPaneComponent from '../../components/filtering/ResourceConditionsPaneComponent';

const TagRuleContraint = [
    { value: 'AtLeastOne', label: 'At least one of the group tags is assigned' },
    { value: 'ExactlyOne', label: 'Exactly one of the group tags is assigned' },
]

const AllColors = [
    "#f07474",
    "#e2c3c3",
    "#d07d55",
    "#e4ad10",
    "#f2d98f",
    "#c9dc2a",
    "#d3dc89",
    "#a0e461",
    "#61e46b",
    "#639a67",
    "#3bba97",
    "#5ec4d4",
    "#96bcc2",
    "#7292cc",
    "#a376c0",
    "#f08aed",
    "#e86080",
    "#e86060",
];

class TagGroupFormComponent extends React.Component {

    constructor(props) {
        super(props)

        this.state = {
            name: "",
            resourceType: "Product",
            color: "#f07474",
            availableTags: [],
            selectedTags: [],
            modalActive: false,
            ruleEnabled: false,
            ruleConstraint: "AtLeastOne",
            ruleFilterLogicalOperator: "And",
            ruleFilterChildren: [],
        };

        if (props.tagGroup != null) {
            this.state.name = props.tagGroup.name;
            this.state.resourceType = props.tagGroup.resourceType;
            this.state.color = props.tagGroup.color;
            this.state.selectedTags = props.tagGroup.tags;
            
            if (props.tagGroup.rule != null) {
                this.state.ruleEnabled = true;
                this.state.ruleConstraint = props.tagGroup.rule.ruleConstraint;
                this.state.ruleFilterLogicalOperator = props.tagGroup.rule.ruleFilterLogicalOperator;
                this.state.ruleFilterChildren = props.tagGroup.rule.ruleFilterChildren;
            }
        };
    }

    onSelectColor = (newColor) => {
        this.setState({ color: newColor });
    }

    render() {

        var buttons = AllColors.map(_ => <Button pressed={this.state.color === _} onClick={() => this.onSelectColor(_)}><div style={{ width: '25px', height: '25px', background: _ }}></div></Button>);
        var hasName = this.state.name != null && this.state.name !== "";
        var hasTags = this.state.selectedTags.length > 0;
        var canSave = hasName && hasTags;

        var resourceTypeSettings = ResourceTypeSettings[this.state.resourceType];
        var rulePrimaryAction = { content: 'Disable Rule', destructive: true, onAction: () => { this.handleDisableRule(); } };

        if (!this.state.ruleEnabled) {
            rulePrimaryAction = { content: 'Enable Rule', onAction: () => { this.handleEnableRule(); } };
        }

        return (
            <div>
                <Helmet>
                    <title>Tag Genie - Create Tag Group</title>
                </Helmet>
                <Page
                    title="Create Tag Group"
                    primaryAction={{ content: 'Save', onAction: this.handleSave, disabled: !canSave }}
                    breadcrumbs={[{ content: 'Tag Groups', url: '/tag-groups' }]}
                >
                    <Layout fullWidth>
                        <Layout.Section fullWidth>
                            <Card>
                                <Card.Section title="Resource type">
                                    <Select options={ResourceTypes} value={this.state.resourceType} onChange={this.handleResourceTypeChange} />
                                </Card.Section>
                            </Card>
                            <Card sectioned title="Name & Color">

                                <p>Choose a name so that you can easily distinguish dirrent tag groups - for example, Sizes, Colors, Types. Color can also help to identify different groups.</p>
                                <br />
                                <FormLayout>
                                    <TextField
                                        label="Name"
                                        value={this.state.name}
                                        error={hasName ? null : "Must have name"}
                                        onChange={this.handleNameChange}
                                        autoComplete="off"
                                    />
                                    <Stack>{buttons}</Stack>
                                </FormLayout>
                            </Card>
                            <Card sectioned title="Tags"
                                primaryFooterAction={{ content: 'Choose tags', onAction: this.onChooseTags }}>
                                {this.state.modalActive && <ChooseTagsModal
                                    active={this.state.modalActive}
                                    onCancel={this.onModalCancel}
                                    onDone={this.onModalDone}
                                    selectedTags={this.state.selectedTags}
                                    tags={this.state.availableTags}
                                />}
                                <p>Choose tags that belong to this group. Usually tags groups are composed of tags that describe the same attribute - for example, sizes, colors, types, etc.</p>
                                <br />
                                {!hasTags && <InlineError message="At least one tag must be selected" />}
                                <Stack>
                                    {this.state.selectedTags.map(item => <GenieTag backgroundColor={this.state.color}>{item}</GenieTag>)}
                                </Stack>
                            </Card>

                            <Card title={`${resourceTypeSettings.singularStart} Validation Rule`}
                                primaryFooterAction={rulePrimaryAction}>
                                {this.state.ruleEnabled &&
                                    <div>
                                        <Card.Section title="Rule Constraint">
                                            <p>{`The constraint specifies how ${resourceTypeSettings.singular} items must adhere to be considered valid.`}</p>
                                            <br />
                                            <Select options={TagRuleContraint} value={this.state.ruleConstraint} onChange={this.handleConstraintChange} />
                                        </Card.Section>
                                        <Card.Section title="Matching Condition">
                                            <p>{`The matching conditions specifies which ${resourceTypeSettings.plural} will be validated against this tag rule. If no condition is set, all ${resourceTypeSettings.plural} in the store will be validated.`}</p>
                                            <br />
                                            <ResourceConditionsPaneComponent
                                                resourceSingular={resourceTypeSettings.singular}
                                                resourcePlural={resourceTypeSettings.plural}
                                                resourceType={this.state.resourceType}
                                                autoUpdateFilters={true}
                                                expanded={true}
                                                tags={[]}
                                                filters={this.state.ruleFilterChildren}
                                                filtersLogicalOperator={this.state.ruleFilterLogicalOperator}
                                                onFiltersUpdated={this.handleFiltersUpdated}
                                            />
                                        </Card.Section>
                                    </div>
                                }
                            </Card>
                        </Layout.Section>
                    </Layout>
                </Page>
            </div >
        );
    }

    handleFiltersUpdated = (updatedFilters, filtersLogicalOperator) => {
        this.setState({
            ruleFilterChildren: updatedFilters,
            ruleFilterLogicalOperator: filtersLogicalOperator
        });
    }

    handleConstraintChange = (value) => {
        this.setState({
            ruleConstraint: value,
        });
    }

    handleEnableRule = () => {
        this.setState({
            ruleEnabled: true,
            ruleConstraint: "AtLeastOne",
            ruleFilterLogicalOperator: "And",
            ruleFilterChildren: [],
        });
    }

    handleDisableRule = () => {

        this.setState({
            ruleEnabled: false,
            ruleConstraint: "AtLeastOne",
            ruleFilterLogicalOperator: "And",
            ruleFilterChildren: [],
        });
    }

    onModalCancel = () => {
        this.setState({
            modalActive: false
        });
    }

    onModalDone = (newSelectedTags) => {
        this.setState({
            modalActive: false,
            selectedTags: newSelectedTags.sort()
        });
    }

    onChooseTags = () => {
        this.setState({
            modalActive: true
        });
    }

    handleTagSelected = (selected) => {
        if (selected.length <= 0) {
            return;
        }

        var selectedTag = selected[0];
        var selectedIndex = this.state.selectedTags.findIndex(tag => tag === selectedTag);
        var newSelected = [];

        if (selectedIndex >= 0) {
            newSelected = [...this.state.selectedTags.slice(0, selectedIndex), ...this.state.rule.tagList.slice(selectedIndex + 1)];
        }
        else {

            newSelected = [...this.state.selectedTags, selectedTag];
        }

        this.setState({
            selectedTags: newSelected,
            tagText: selectedTag
        });
    }

    handleTagClicked = (tag) => {
        this.handleTagSelected([tag]);
    }

    updateText = (value) => {
        this.setState({ tagText: value });

        if (value === '') {
            this.setState({
                matchingTags: this.state.availableTags.map((item, index) => {
                    return {
                        label: item,
                        value: item,
                    }
                }
                )
            });
            return;
        }

        const filterRegex = new RegExp(value, 'i');
        const resultOptions = this.state.availableTags.filter((option) => {
            return option.match(filterRegex)
        });

        this.setState({
            matchingTags: resultOptions.map((item, index) => {
                return {
                    label: item,
                    value: item,
                }
            }
            )
        });
    }

    handleResourceTypeChange = (value) => {
        this.setState({ resourceType: value }, () => this.fetchTags());
    }

    handleNameChange = (value) => {
        this.setState({ name: value });
    }

    setColor = (value) => {
        this.setState({ color: value });
    }

    handleSave = () => {

        var group = {
            name: this.state.name,
            color: this.state.color,
            tags: this.state.selectedTags,
            resourceType: this.state.resourceType,
            rule: null
        };

        if (this.state.ruleEnabled) {

            const mappedFilters = this.state.ruleFilterChildren.map((_) => _.body);
            var rule = {
                constraint: this.state.ruleConstraint,
                filter: {
                    filterType: "composite",
                    children: mappedFilters,
                    logicalOperator: this.state.ruleFilterLogicalOperator,
                }
            }

            group.rule = rule;
        }

        this.props.onAction(group);
    }

    fetchTags = () => {

        var url = `api/products/tags`;

        if (this.state.resourceType === "Customer") {
            url = "api/customers/tags";
        }

        fetch(url, {
            method: 'GET'
        })
            .then(response => response.json())
            .then(data => this.setState({
                availableTags: data.map(_ => { return { tagName: _ } })
            }));

    }

    componentDidMount() {
        this.fetchTags();
    }
}

export default TagGroupFormComponent;